import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { MetricsService } from 'src/app/services/metrics.service';
import * as dayjs from 'dayjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, take, takeUntil } from 'rxjs/operators';
import { ObjectListService, ObjectNode } from 'src/app/services/object-list.service';
import { MatDialog } from '@angular/material/dialog';
import { FaultDetailsDialog } from '../fault-details-dialog/fault-details-dialog.component';
import { FilterComponent } from 'src/app/components/problem-list/filter/filter.component';
import { FilterAnalysisDialog } from '../filter-analysis-dialog/filter-analysis-dialog.component';
import { GeaMesDatepickerModule } from '@gea-mes/datepicker';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { graphic } from 'echarts';

@Component({
    selector: 'app-pareto',
    templateUrl: './pareto.component.html',
    styleUrls: ['./pareto.component.scss'],
    imports: [GeaMesDatepickerModule, MatTooltip, MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, NgFor, MatOption, MatRadioGroup, MatRadioButton, MatButton, MatIcon, NgIf, NgxEchartsDirective, MatProgressSpinner]
})
export class ParetoComponent implements OnInit, OnDestroy {

  @Input() DatePickerIndex:number = 0;

  objectid:string;
  site:string;

  object:ObjectNode;

  cycleTime:number;
  cycleTimeRetrieved$: Subject<boolean> = new Subject<boolean>(); // Used in takeUntil to stop subscription when cycle time is retrieved
  chartOptions: EChartsOption = {};
  formattedData:any[] = [{ label: '', value: 0, drilldown: 0 }]; // To avoid console erorr, need to default the data to something.
  filteredData:any[] = [{ label: '', value: 0, drilldown: 0 }]; // To avoid console erorr, need to default the data to something.
  filterData:any[] = [];

  shifts:any[] = ['1ST SHIFT','2ND SHIFT','3RD SHIFT'];

  valueFilterType1:string = 'all';
  valueFilterType2:string = 'all';
  valueFilterType3:string = 'all';
  valueFiltersLevel1:string[] = [];
  valueFiltersLevel2:string[] = [];
  valueFiltersLevel3:string[] = [];
  filterCount:number = 0;

  shiftSelect = new FormControl();

  paertoSub$: Subscription;
  paretoData:any = {};

  // Parameters for pulling Pareto data
  level:number = 1;
  startDate:string;
  endDate:string;
  filterLevel1:string = '';
  filterLevel2:string = '';
  minSeconds:number = 0;

  labelSeperator:string = '->';
  showDetail:boolean = false;
  showBy:string = 'duration';

  loadingData:boolean = true;
  hasData: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private svcMetrics: MetricsService,
    private svcObjectList: ObjectListService,
    private zone: NgZone,
    private dialog: MatDialog
  ) { 
    this.setChartData(); // Need to set the default chart config even though we don't have data yet
    this.site = this.route.snapshot.paramMap.get('site');
    this.objectid = this.route.snapshot.paramMap.get('objectid');
    this.shiftSelect.setValue(this.shifts);
  }
  
  ngOnInit(): void {
    // Load the Object Config
    this.svcObjectList.objectList().subscribe(() => {
        // Now we know we've got an objectlist, look up the object config. This prevents problems when deep linking to the analysis page
        this.svcObjectList.getObjectConfig(this.site, this.objectid).pipe(filter(out => out != null), take(1)).subscribe(
          (objectNode:ObjectNode) => {
            this.object = objectNode;
            
            this.getData();
          }
        );

    });

  }
 
  applyFilter() {
    // Find the filter type and values for this level
    let valueFilterType: string;
    let valueFilters: string[];

    switch (this.level) {
      case 1:
        valueFilterType = this.valueFilterType1;
        valueFilters = this.valueFiltersLevel1;
        break;
      case 2:
        valueFilterType = this.valueFilterType2;
        valueFilters = this.valueFiltersLevel2;
        break;
      case 3:
        valueFilterType = this.valueFilterType3;
        valueFilters = this.valueFiltersLevel3;
        break;
      default:
        valueFilterType = 'all';
        valueFilters = [];
        break;
    }

    // Filter the data to include/exclude if needed
    if (valueFilterType !== 'all' && valueFilters.length > 0) {
      if (valueFilterType === 'exclude') {
        this.filteredData = this.formattedData.filter(rec => !valueFilters.includes(rec.label));
      } else if (valueFilterType === 'include') {
        this.filteredData = this.formattedData.filter(rec => valueFilters.includes(rec.label));
      } else {
        this.filteredData = [...this.formattedData];
      }
    } else {
      this.filteredData = this.formattedData.filter(rec => !valueFilters.includes(rec.label));
    }
    
    this.filteredData.sort((n1,n2) => n2.value - n1.value);

    this.hasData = this.filteredData.length > 0;

    if (this.hasData) {
      this.setChartData();
    } else {
      this.chartOptions = {};
    }
  }
           

  getData() {
    // First get the Ideal Cycle Time from header and then start pulling Pareto data
    this.svcMetrics.metricsHeader(this.object).pipe(takeUntil(this.cycleTimeRetrieved$)).subscribe(
      out => {
        // Check if we got a return, possible to get a null due to caching in the service
        if (out) {
          this.cycleTime = out.Body.PRODUCTION.idealCycleTime;
          this.getParetoData();
          
          this.cycleTimeRetrieved$.next(true); // Once we have the cycle time, set Subject to true to force this to end this subscription.
          this.svcMetrics.cleanupObservers();
        }
      }
    );
  }

  clearFilter(level:number = 1) {
    this.level = level;
    if (level === 1) {
      this.filterLevel1 ='';
      this.filterLevel2 ='';
    } else {
      this.filterLevel2 = '';
    }
    this.setFilterCount();
    this.getParetoData();
  }

  onDateSelection(e: any) {
    this.startDate = dayjs(e.start).format('YYYY-MM-DD');
    this.endDate = dayjs(e.end).format('YYYY-MM-DD');
    this.getParetoData();
  }

  setMinSeconds() {
    const dialogRef = this.dialog.open(FilterComponent, {
      width: '250px',
      maxHeight: '800px',
      data: {
        minDuration: this.minSeconds
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.minSeconds = result.minDuration;
        this.getData();
      }
    });    
  }

  openFilterDialog() {
    // Determine filter type and values for this level
    let valueFilterType:string;
    let valueFilters:string[];

    switch (this.level) {
      case 1:
        valueFilterType = this.valueFilterType1;
        valueFilters = this.valueFiltersLevel1;
        break;
      case 2:
        valueFilterType = this.valueFilterType2;
        valueFilters = this.valueFiltersLevel2;
        break;
      case 3:
        valueFilterType = this.valueFilterType3;
        valueFilters = this.valueFiltersLevel3;
        break;
      default:
        valueFilterType = 'all';
        valueFilters = [];
        break;
    }

    const dialogRef = this.dialog.open(FilterAnalysisDialog, {
      width: '450px',
      maxHeight: '800px',
      data: {
        minDuration: this.minSeconds,
        formattedData: this.formattedData,
        valueFilterType,
        valueFilters
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updateFilter) {
          switch (this.level) {
            case 1:
              this.valueFilterType1 = result.valueFilterType;
              this.valueFiltersLevel1 = result.valueFilters;
              break;
            case 2:
              this.valueFilterType2 = result.valueFilterType;
              this.valueFiltersLevel2 = result.valueFilters;
              break;
            case 3:
              this.valueFilterType3 = result.valueFilterType;
              this.valueFiltersLevel3 = result.valueFilters;
              break;
          }

        this.applyFilter();

        if (this.minSeconds !== result.minDuration) {
          this.minSeconds = result.minDuration;
          this.getData();
        }

        this.setFilterCount();
      }
    });
  }

  setFilterCount(): void {
    this.filterCount = 0;

    if (this.minSeconds > 0) {
      this.filterCount += 1;
    }

    switch (this.level) {
      case 1:
        if (this.valueFilterType1 !== 'all') this.filterCount += 1;
        break;
      case 2:
        if (this.valueFilterType2 !== 'all') this.filterCount += 1;
        break;
      case 3:
        if (this.valueFilterType3 !== 'all') this.filterCount += 1;
        break;
    }
  }

  getParetoData() {
  if (this.paertoSub$) this.paertoSub$.unsubscribe();

  const myLevel: number = this.showDetail ? 3 : this.level;
  this.loadingData = true;

  this.paertoSub$ = this.svcMetrics
    .pareto(
      this.site,
      this.objectid,
      this.startDate,
      this.endDate,
      myLevel,
      this.filterLevel1,
      this.filterLevel2,
      this.shiftSelect.value.toString(),
      this.minSeconds
    )
    .subscribe({
      next: (data) => {
        this.paretoData = data?.Body || [];

        const myFormattedData: any[] = [];

        this.paretoData.forEach(element => {
          let myLabel: string = element.Level1;

          if ((this.showDetail || this.level > 1) && element.Level2 !== '') {
            myLabel = myLabel.concat(this.labelSeperator + element.Level2);
          }

          if ((this.showDetail || this.level > 2) && element.Level3 !== '') {
            myLabel = myLabel.concat(this.labelSeperator + element.Level3);
          }

          let myValue: number;

          if (this.showBy === 'duration') {
            myValue = element.Downtime;
          } else if (this.showBy === 'occurences') {
            myValue = element.Occurences;
          } else {
            myValue = Math.floor(element.Downtime / this.cycleTime);
          }

          myFormattedData.push({
            label: myLabel,
            value: myValue,
            drilldown: element.AdditionalLevel
          });
        });

        this.formattedData = myFormattedData;
        this.loadingData = false;
        this.applyFilter();
      },
      error: (err) => {
        console.error('pareto error', err);
        this.formattedData = [];
        this.filteredData = [];
        this.hasData = false;
        this.chartOptions = {};
        this.loadingData = false;
      }
    });
}

  setGrouping(grouping: string) {
    this.showBy = grouping;
    this.getParetoData();
  }

 setChartData() {
  let pYAxisName = 'Lost Units';

  if (this.showBy === 'duration') {
    pYAxisName = 'Duration (seconds)';
  } else if (this.showBy === 'occurences') {
    pYAxisName = 'Occurences';
  }

  const labels = this.filteredData.map(x => x.label);
  const values = this.filteredData.map(x => x.value);

  const shouldAlternateLabels = labels.length > 60;

  const total = values.reduce((sum, val) => sum + val, 0) || 1;
  let running = 0;

  const cumulative = values.map(val => {
    running += val;
    return Number(((running / total) * 100).toFixed(2));
  });

  const maxLabelLength = Math.max(...labels.map(x => (x ? x.length : 0)), 0);
  const dynamicBottom = Math.min(Math.max(160, maxLabelLength * 6), 320);

  this.chartOptions = {
    animation: false,
    grid: {
      left: 80,
      right: 80,
      top: 30,
      bottom: dynamicBottom,
      containLabel: false
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const barPoint = Array.isArray(params)
          ? params.find((p: any) => p.seriesType === 'bar')
          : params;

        const linePoint = Array.isArray(params)
          ? params.find((p: any) => p.seriesType === 'line')
          : null;

        return `
          <div>
            <strong>Root Cause:</strong> ${barPoint?.axisValue ?? ''}<br/>
            <strong>${pYAxisName}:</strong> ${barPoint?.value ?? 0}<br/>
            <strong>Cumulative Percentage:</strong> ${linePoint?.value ?? 0}%
          </div>
        `;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Root Cause',
      nameLocation: 'middle',
      nameGap: 130,
      nameTextStyle: {
        fontSize: 12,
        color: '#666'
      },
      data: labels,
      axisTick: {
        alignWithLabel: true
      },
      axisLine: {
        lineStyle: {
          color: '#cfcfcf'
        }
      },
      axisLabel: {
        interval: 0,
        rotate: 90,
        fontSize: 12,
        color: '#666',
        margin: 14,
        hideOverlap: false,
        formatter: (value: string, index: number) => {
          if (shouldAlternateLabels && index % 2 === 1) {
            return '';
          }

          return this.truncateAxisLabel(value, 40);
        }
      }
    },
    yAxis: [
      {
        type: 'value',
        name: pYAxisName,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 60,
        nameTextStyle: {
          fontSize: 12,
          color: '#666'
        },
        min: 0,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#d9dde5',
            width: 1
          }
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          formatter: (value: number) => this.formatCompactNumber(value)
        }
      },
      {
        type: 'value',
        name: 'Cumulative Percentage',
        nameLocation: 'middle',
        nameRotate: 270,
        nameGap: 40,
        min: 0,
        max: 100,
        splitLine: {
          show: false
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: '{value}%',
          color: '#666'
        }
      }
    ],
    series: [
      {
        name: pYAxisName,
        type: 'bar',
        data: values,
        barMaxWidth: 18,
        itemStyle: {
          borderRadius: [8, 8, 0, 0],
          color: new graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#c7cdfa' },
            { offset: 0.35, color: '#8f98db' },
            { offset: 0.7, color: '#6670b8' },
            { offset: 1, color: '#4a538f' }
          ])
        },
        emphasis: {
          focus: 'series'
        }
      },
      {
        name: 'Cumulative Percentage',
        type: 'line',
        yAxisIndex: 1,
        data: cumulative,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#666'
        },
        itemStyle: {
          color: '#666'
        }
      }
    ]
  };
}

  private formatCompactNumber(value: number): string {
    if (value >= 1000) {
      return `${Math.round(value / 1000)}k`;
    }
    return `${value}`;
  }

  private truncateAxisLabel(value: string, maxLength: number = 40): string {
    if (!value) return '';

    const cleaned = value.replace(/\s+/g, ' ').trim();

    if (cleaned.length <= maxLength) {
      return cleaned;
    }

    return `${cleaned.slice(0, maxLength)}…`;
  }

  onChartClick(event: any) {
    const clickedIndex = event?.dataIndex;
    if (typeof clickedIndex !== 'number') return;

    this.zone.run(() => {
      this.drilldownByIndex(clickedIndex);
    });
  }

  private drilldownByIndex(clickedItem: number) {
    const selected = this.filteredData[clickedItem];
    if (!selected) return;

    const levelSelected: string = selected.label;

    if (selected.drilldown === 0) {
      const arrLabel = levelSelected.split(this.labelSeperator);

      const level1: string = arrLabel.length >= 1 ? arrLabel[0] : '';
      const level2: string = arrLabel.length >= 2 ? arrLabel[1] : '';
      const level3: string = arrLabel.length >= 3 ? arrLabel[2] : '';

      this.openFaultDetails(level1, level2, level3);
    } else {
      const arrLabel = levelSelected.split(this.labelSeperator);

      if (this.filterLevel1 === '') {
        this.level = 2;
        this.filterLevel1 = arrLabel[0];
        this.valueFilterType2 = 'all'; // Reset value filters for this level
        this.valueFiltersLevel2 = []; // Rest value filters for this level
      } else {
        this.level = 3;
        this.filterLevel2 = arrLabel[1];
        this.valueFilterType3 = 'all'; // Reset value filters for this level
        this.valueFiltersLevel3 = []; // Rest value filters for this level
      }

      this.setFilterCount();
      this.getParetoData();
    }
  }

  openFaultDetails(level1: string, level2: string, level3: string) {
    const dialogRef = this.dialog.open(FaultDetailsDialog, {
      width: '1200px',
      maxHeight: '900px',
      data: {
        objectid: this.object.ObjectID,
        site: this.site,
        environment: this.object.Environment,
        startDate: this.startDate,
        endDate: this.endDate,
        shifts: this.shiftSelect.value,
        level1,
        level2,
        level3,
        objectType: this.object.ObjectType,
        minDuration: this.minSeconds
      }
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  /**
   * Toggles between showing just top level and showing all levels
   */
  toggleDetail() {
    this.showDetail = !this.showDetail;
    this.getParetoData();
  }
  
  ngOnDestroy(): void { 
    if (this.paertoSub$) this.paertoSub$.unsubscribe();
    this.cycleTimeRetrieved$.next(true); // Force cycle time sub to end
  }

}
